<template>
  <f7-page name="tontine-setup">
    <f7-navbar :title="$t('tontine.setup.title')" back-link no-shadow>
      <f7-nav-right>
        <span class="step-label">{{
          $t("tontine.setup.stepLabel", { step, total: 4 })
        }}</span>
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <div class="step-bar">
          <div
            v-for="n in 4"
            :key="n"
            class="step-dot"
            :class="{ on: n <= step }"
          />
        </div>

        <!-- Step 1: Operating Mode -->
        <f7-block v-if="step === 1" strong inset>
          <h2>{{ $t("tontine.setup.step1Title") }}</h2>
          <p class="text-color-gray">
            {{ $t("tontine.setup.step1Intro") }}
          </p>

          <f7-list media-list menu-list>
            <f7-list-item
              radio
              name="operating-mode"
              value="COORDINATION_ONLY"
              :checked="form.operatingMode === 'COORDINATION_ONLY'"
              @change="form.operatingMode = 'COORDINATION_ONLY'"
              :title="$t('tontine.setup.modeCard.Coordination.title')"
              :footer="$t('tontine.setup.modeCard.Coordination.footer')"
            >
              <template #media
                ><f7-icon f7="calendar_badge_plus" color="blue"
              /></template>
            </f7-list-item>

            <f7-list-item
              radio
              name="operating-mode"
              value="PARTNER_PAYMENT_ORCHESTRATED"
              :checked="form.operatingMode === 'PARTNER_PAYMENT_ORCHESTRATED'"
              @change="form.operatingMode = 'PARTNER_PAYMENT_ORCHESTRATED'"
              :title="$t('tontine.setup.modeCard.Partner.title')"
              :footer="$t('tontine.setup.modeCard.Partner.footer')"
            >
              <template #media
                ><f7-icon f7="link_circle_fill" color="green"
              /></template>
            </f7-list-item>

            <f7-list-item
              radio
              name="operating-mode"
              value="REGULATED_PARTNER_SAVINGS"
              :checked="form.operatingMode === 'REGULATED_PARTNER_SAVINGS'"
              @change="form.operatingMode = 'REGULATED_PARTNER_SAVINGS'"
              :title="$t('tontine.setup.modeCard.Regulated.title')"
              :footer="$t('tontine.setup.modeCard.Regulated.footer')"
            >
              <template #media
                ><f7-icon f7="building_columns_fill" color="orange"
              /></template>
            </f7-list-item>
          </f7-list>
        </f7-block>

        <!-- Step 2: Contribution and Schedule -->
        <f7-list media-list v-if="step === 2" inset strong>
          <f7-list-input
            :label="$t('tontine.setup.contribution')"
            type="text"
            inputmode="numeric"
            placeholder="10 000"
            :value="formatMoneyInput(form.contributionAmount)"
            @input="form.contributionAmount = parseMoney($event.target.value)"
          >
            <template #media>
              <span style="padding-right: 5px; color: var(--f7-theme-color)">{{
                form.currency || props.campaign.currency
              }}</span>
            </template>
          </f7-list-input>
          <f7-list-item
            :title="$t('tontine.setup.frequency')"
            smart-select
            :smart-select-params="{
              openIn: isSmallScreen ? 'sheet' : 'popover',
              closeOnSelect: true,
              sheetSwipeToClose: true,
            }"
          >
            <select v-model="form.frequency">
              <option value="WEEKLY">
                {{ $t("tontine.frequency.WEEKLY") }}
              </option>
              <option value="BIWEEKLY">
                {{ $t("tontine.frequency.BIWEEKLY") }}
              </option>
              <option value="MONTHLY">
                {{ $t("tontine.frequency.MONTHLY") }}
              </option>
              <option value="QUARTERLY">
                {{ $t("tontine.frequency.QUARTERLY") }}
              </option>
              <option value="CUSTOM">
                {{ $t("tontine.frequency.CUSTOM") }}
              </option>
            </select>
          </f7-list-item>
          <f7-list-input
            v-if="form.frequency === 'CUSTOM'"
            :label="$t('tontine.setup.customDays')"
            type="number"
            v-model:value="form.customFrequencyDays"
          />
          <f7-list-input
            :label="$t('tontine.setup.firstDate')"
            type="datepicker"
            readonly
            :calendar-params="calendarParams"
            :value="form.firstContributionDate"
            @calendar:change="form.firstContributionDate = $event[0]"
          />
          <f7-list-input
            :label="$t('tontine.setup.gracePeriod')"
            type="number"
            v-model:value="form.gracePeriodDays"
            :info="$t('tontine.setup.graceInfo')"
          />
        </f7-list>

        <!-- Step 3: Allocation and Validation -->
        <f7-block v-if="step === 3" strong inset>
          <h2>{{ $t("tontine.setup.step3Title") }}</h2>
          <p class="text-color-gray">
            {{ $t("tontine.setup.step3Intro") }}
          </p>

          <f7-list>
            <f7-list-item
              :title="$t('tontine.setup.allocation')"
              smart-select
              :smart-select-params="{
                openIn: isSmallScreen ? 'sheet' : 'popover',
                closeOnSelect: true,
                sheetSwipeToClose: true,
              }"
            >
              <select v-model="form.allocationMethod">
                <option value="FIXED_ROTATION">
                  {{ $t("tontine.allocation.FIXED_ROTATION") }}
                </option>
                <option value="RANDOM_DRAW">
                  {{ $t("tontine.allocation.RANDOM_DRAW") }}
                </option>
                <option value="MEMBER_VOTE">
                  {{ $t("tontine.allocation.MEMBER_VOTE") }}
                </option>
                <option value="COMMITTEE_APPROVAL">
                  {{ $t("tontine.allocation.COMMITTEE_APPROVAL") }}
                </option>
                <option value="NEED_BASED">
                  {{ $t("tontine.allocation.NEED_BASED") }}
                </option>
              </select>
            </f7-list-item>
            <f7-list-input
              :label="$t('tontine.setup.approvals')"
              type="number"
              v-model:value="form.requiredPayoutApprovals"
              :info="$t('tontine.setup.approvalsInfo')"
            />
            <f7-list-item
              :title="$t('tontine.setup.visibility')"
              smart-select
              :smart-select-params="{
                openIn: isSmallScreen ? 'sheet' : 'popover',
                closeOnSelect: true,
                sheetSwipeToClose: true,
              }"
            >
              <select v-model="form.financialVisibility">
                <option value="MEMBER_TOTALS_ONLY">
                  {{ $t("tontine.visibility.MEMBER_TOTALS_ONLY") }}
                </option>
                <option value="MEMBERS_SEE_NAMES_NOT_AMOUNTS">
                  {{ $t("tontine.visibility.MEMBERS_SEE_NAMES_NOT_AMOUNTS") }}
                </option>
                <option value="MEMBERS_SEE_NAMES_AND_AMOUNTS">
                  {{ $t("tontine.visibility.MEMBERS_SEE_NAMES_AND_AMOUNTS") }}
                </option>
                <option value="FINANCE_ADMINS_ONLY">
                  {{ $t("tontine.visibility.FINANCE_ADMINS_ONLY") }}
                </option>
              </select>
            </f7-list-item>
            <f7-list-item
              checkbox
              :title="$t('tontine.setup.beneficiaryVerification')"
              :checked="form.requireBeneficiaryVerification"
              @checkbox:change="form.requireBeneficiaryVerification = $event"
            />
          </f7-list>
        </f7-block>

        <!-- Step 4: Review and Activate -->
        <f7-block v-if="step === 4" strong inset>
          <h2>{{ $t("tontine.setup.step4Title") }}</h2>
          <div class="review-row">
            <span>{{ $t("tontine.setup.reviewContribution") }}</span
            ><strong>{{
              money(form.contributionAmount, form.currency)
            }}</strong>
          </div>
          <div class="review-row">
            <span>{{ $t("tontine.setup.reviewFrequency") }}</span
            ><strong>{{ frequencyLabel }}</strong>
          </div>
          <div class="review-row">
            <span>{{ $t("tontine.setup.reviewAllocation") }}</span
            ><strong>{{ allocationLabel }}</strong>
          </div>
          <div class="review-row">
            <span>{{ $t("tontine.setup.reviewApprovals") }}</span
            ><strong>{{ form.requiredPayoutApprovals }}</strong>
          </div>
          <div class="review-row">
            <span>{{ $t("tontine.setup.reviewMode") }}</span
            ><strong>{{ modeLabel }}</strong>
          </div>
          <p class="notice">
            {{ $t("tontine.setup.notice") }}
          </p>
        </f7-block>

        <f7-toolbar bottom class="create-footer no-hairlines">
          <div
            :class="step > 1 ? 'grid cols25_75 grid-gap' : ''"
            class="width-100 margin-horizontal"
          >
            <f7-button v-if="step > 1" outline @click="step -= 1">{{
              $t("tontine.setup.back")
            }}</f7-button>
            <f7-button
              fill
              :disabled="!canContinue"
              @click="next"
              :class="step > 1 ? 'margin-right' : ''"
            >
              {{
                step === 4
                  ? $t("tontine.setup.create")
                  : $t("tontine.setup.continue")
              }}
            </f7-button>
          </div>
        </f7-toolbar>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { computed, reactive, ref, onMounted, watch } from "vue";
import { f7 } from "framework7-vue";
import { useI18n } from "vue-i18n";
import AppLayout from "../../components/common/AppLayout.vue";
import { useSmallScreen } from "../../js/composables/useMediaQuery.js";

const { t } = useI18n();
const props = defineProps({ campaignId: String, groupId: String });
const store = useTontineStore();
const step = ref(1);
const DRAFT_KEY = computed(
  () => `avocash:tontine-setup:${props.campaignId}:v1`,
);
const isSmallScreen = useSmallScreen().matches;

const form = reactive({
  operatingMode: "COORDINATION_ONLY",
  contributionAmount: "",
  currency: props.campaign.currency,
  frequency: "MONTHLY",
  customFrequencyDays: null,
  firstContributionDate: null,
  gracePeriodDays: 0,
  latePaymentFee: 0,
  allocationMethod: "FIXED_ROTATION",
  requiredPayoutApprovals: 1,
  financialVisibility: "MEMBER_TOTALS_ONLY",
  requireBeneficiaryVerification: false,
  paymentPartnerCode: null,
});

const calendarParams = {
  closeOnSelect: true,
  minDate: new Date(),
  dateFormat: "dd MM yyyy",
};
const canContinue = computed(() => {
  if (step.value === 1) return !!form.operatingMode;
  if (step.value === 2)
    return (
      Number(form.contributionAmount) > 0 &&
      !!form.frequency &&
      !!form.firstContributionDate
    );
  if (step.value === 3)
    return !!form.allocationMethod && Number(form.requiredPayoutApprovals) >= 1;
  return true;
});

function next() {
  if (!canContinue.value) return;
  if (step.value < 4) {
    step.value += 1;
    return;
  }
  submit();
}

async function submit() {
  f7.preloader.show();
  try {
    await store.create(props.campaignId, {
      ...form,
      contributionAmount: Number(form.contributionAmount),
      gracePeriodDays: Number(form.gracePeriodDays || 0),
      latePaymentFee: Number(form.latePaymentFee || 0),
      requiredPayoutApprovals: Number(form.requiredPayoutApprovals),
      firstContributionDate: toLocalDate(form.firstContributionDate),
    });
    localStorage.removeItem(DRAFT_KEY.value);
    f7.views.main.router.navigate(
      `/groups/${props.groupId}/campaigns/${props.campaignId}/tontine/`,
    );
  } catch (error) {
    f7.dialog.alert(error.message || t("tontine.loadError"));
  } finally {
    f7.preloader.hide();
  }
}

function parseMoney(value) {
  return String(value || "").replace(/[^0-9]/g, "");
}
function formatMoneyInput(value) {
  return value ? new Intl.NumberFormat("fr-FR").format(Number(value)) : "";
}
function money(value, currency) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: currency || "XOF",
    maximumFractionDigits: 0,
  }).format(value || 0);
}
function toLocalDate(value) {
  const d = Array.isArray(value) ? value[0] : value;
  return d ? new Date(d).toISOString().slice(0, 10) : null;
}

watch(
  () => JSON.stringify({ step: step.value, form }),
  () =>
    localStorage.setItem(
      DRAFT_KEY.value,
      JSON.stringify({ step: step.value, form }),
    ),
);
onMounted(() => {
  const raw = localStorage.getItem(DRAFT_KEY.value);
  if (raw) {
    const draft = JSON.parse(raw);
    Object.assign(form, draft.form);
    step.value = draft.step || 1;
  }
});

const frequencyLabel = computed(
  () =>
    ({
      WEEKLY: t("tontine.frequency.WEEKLY"),
      BIWEEKLY: t("tontine.frequency.BIWEEKLY"),
      MONTHLY: t("tontine.frequency.MONTHLY"),
      QUARTERLY: t("tontine.frequency.QUARTERLY"),
      CUSTOM: t("tontine.frequency.CUSTOM"),
    }[form.frequency]),
);
const allocationLabel = computed(
  () =>
    ({
      FIXED_ROTATION: t("tontine.allocation.FIXED_ROTATION"),
      RANDOM_DRAW: t("tontine.allocation.RANDOM_DRAW"),
      MEMBER_VOTE: t("tontine.allocation.MEMBER_VOTE"),
      COMMITTEE_APPROVAL: t("tontine.allocation.COMMITTEE_APPROVAL"),
      NEED_BASED: t("tontine.allocation.NEED_BASED"),
    }[form.allocationMethod]),
);
const modeLabel = computed(
  () =>
    ({
      COORDINATION_ONLY: t("tontine.mode.COORDINATION_ONLY"),
      PARTNER_PAYMENT_ORCHESTRATED: t(
        "tontine.mode.PARTNER_PAYMENT_ORCHESTRATED",
      ),
      REGULATED_PARTNER_SAVINGS: t("tontine.mode.REGULATED_PARTNER_SAVINGS"),
    }[form.operatingMode]),
);
</script>

<style scoped>
.step-bar {
  display: flex;
  gap: 4px;
  padding: 8px 16px;
}
.step-dot {
  height: 4px;
  flex: 1;
  border-radius: 3px;
  background: #e5e7eb;
}
.step-dot.on {
  background: var(--f7-theme-color);
}
.step-label {
  font-size: 12px;
  color: var(--f7-theme-color);
}
/* Stretch the main column so the footer sits at the bottom of the viewport. */
:deep(.main_layout) {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - var(--f7-navbar-height, 56px));
  min-height: calc(100dvh - var(--f7-navbar-height, 56px));
}

/* Bottom actions bar: stick to the viewport bottom, static on desktop. */
.create-footer {
  margin-top: auto;
  position: sticky;
  bottom: 0;
  z-index: 20;
  border-top: 1px solid var(--f7-list-item-border-color, #e0e0e0);
  padding: 8px 16px calc(8px + var(--f7-safe-area-bottom, 0px));
  background: var(--f7-page-bg-color, #f5f5f5);
}
@media (min-width: 1200px) {
  .create-footer {
    position: static;
  }
}
.review-row {
  display: flex;
  justify-content: space-between;
  padding: 9px 0;
  border-bottom: 1px solid var(--f7-list-item-border-color);
}
.notice {
  font-size: 12px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant);
  margin: 16px 0 0;
}
</style>
